import api from 'services/api';
import { closeModal } from 'store/modals/actions';
import { MODAL_RUN_USER_AGENT } from 'store/modals/modals';
import { selectActiveWorkspaces } from 'store/Workspace/selectors';
import * as types from './types';
import {
  selectAgentExecutions, selectAgentExecutionSelected, selectAgentList,
  selectAgentsSelected, selectLastSelected, selectTriggerAgentId,
  selectTriggerAgentWs, selectTriggerResolveHostname, selectTriggerMinSeverity,
  selectTriggerMaxSeverity,
  SelectShowCloudAgents
} from './selectors';
import { selectQueryParam } from 'store/Filters/selectors';
import { clearOrderBy, setOrderBy, setPageNumber } from 'store/Filters/actions';
import get from 'lodash/get';
import { cloneDeep } from 'lodash';

export const resetState = () => (dispatch) => dispatch({ type: types.AGENTS_RESET_STATE });

function fetching () {
  return async (dispatch) => {
    dispatch({ type: types.AGENTS_FETCHING });
  };
}

function somethingWentWrong (errors) {
  return async (dispatch) => {
    dispatch({ type: types.AGENTS_FAIL, errorMessage: 'There was an error, please try again.', errors });
  };
}

export function getUserAgents () {
  return async (dispatch, getState) => {
    const state = getState();
    try {
      dispatch(fetching());
      const queryParam = selectQueryParam('userAgents', state);
      const response = await api.agents.fetchUserAgents(queryParam);
      const userAgents = response.rows.map((el) => el);
      dispatch({ type: types.AGENTS_GET_USER_AGENTS, userAgents, count: response.count });
    } catch (e) {
      dispatch(somethingWentWrong());
    }
  };
}

export function getCloudAgents () {
  return async (dispatch, getState) => {
    const state = getState();
    try {
      dispatch(fetching());
      const queryParam = selectQueryParam('cloudAgents', state);
      const response = await api.agents.fetchCloudAgents(queryParam);
      const cloudAgents = response.rows.map((el) => el);
      dispatch({ type: types.AGENTS_GET_CLOUD_AGENTS, cloudAgents, count: response.count });
    } catch (e) {
      dispatch(somethingWentWrong());
    }
  };
}

export function getToken () {
  return async (dispatch) => {
    try {
      const responseSession = await api.faraday.getSession();

      const fd = new FormData();
      fd.set('csrf_token', responseSession.csrf_token);

      const responseToken = await api.agents.fetchToken(fd);
      dispatch({
        type: types.AGENTS_GET_TOKEN, token: responseToken.token, tokenExpiresIn: responseToken.expires_in, tokenDuration: responseToken.total_duration
      });
    } catch (e) {
      dispatch(somethingWentWrong(e));
    }
  };
}

export function getUserAgent (id) {
  return async (dispatch) => {
    try {
      dispatch({ type: types.GET_USER_AGENT_REQUEST });
      const response = await api.agents.fetchAgentData(id);
      dispatch({ type: types.GET_USER_AGENT_SUCCESS, id, agent: response });
    } catch (e) {
      dispatch({ type: types.GET_USER_AGENT_FAIL, errorMessage: get(e, 'message', 'There was an error, please try again.') });
    }
  };
}

export function runUserAgentTool (toolName, toolParams, toolId) {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const agentId = selectTriggerAgentId(state);
      const workspaces = selectTriggerAgentWs(state, toolId);
      const min_severity = selectTriggerMinSeverity(state, toolId);
      const max_severity = selectTriggerMaxSeverity(state, toolId);
      const resolveHostname = selectTriggerResolveHostname(state, toolId);

      dispatch(fetching());

      const toolParamsClone = cloneDeep(toolParams);
      delete toolParamsClone.vuln_tag;
      delete toolParamsClone.service_tag;
      delete toolParamsClone.host_tag;
      delete toolParamsClone.workspaces_names;
      delete toolParamsClone.min_severity;
      delete toolParamsClone.max_severity;
      delete toolParamsClone.resolve_hostname;

      const payload = {
        min_severity,
        max_severity,
        resolve_hostname: resolveHostname,
        workspaces_names: workspaces,
        executor_data: {
          agent_id: agentId,
          executor: toolName,
          args: toolParamsClone
        }
      };
      await api.agents.runUserAgentTool(agentId, payload);

      dispatch(getUserAgents());
      dispatch(closeModal(MODAL_RUN_USER_AGENT));
      dispatch({ type: types.AGENTS_SET_SUCCESS, message: `${toolName} has been started` });
    } catch (e) {
      dispatch(somethingWentWrong(e));
    }
  };
}

export function runCloudAgentTool (wsSelected) {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const agentId = selectTriggerAgentId(state);
      dispatch(fetching());

      await api.agents.runCloudAgent(agentId, wsSelected.map(w => w.primary));

      dispatch(getCloudAgents());
      dispatch(closeModal(MODAL_RUN_USER_AGENT));
      dispatch({ type: types.AGENTS_SUCCESS });
    } catch (e) {
      dispatch(somethingWentWrong(e));
    }
  };
}

export function resetWizard () {
  return async (dispatch) => {
    dispatch({ type: types.AGENTS_CREATE_WIZARD_RESET });
  };
}

export function addTool (toolName) {
  return async (dispatch) => {
    dispatch({ type: types.AGENTS_CREATE_WIZARD_ADD_TOOL, tool: { name: toolName, variables: [] } });
  };
}

export function removeTool (tool) {
  return async (dispatch) => {
    dispatch({ type: types.AGENTS_CREATE_WIZARD_REMOVE_TOOL, tool });
  };
}

export function setFieldsTool (toolName, fields) {
  return async (dispatch) => {
    dispatch({ type: types.AGENTS_CREATE_WIZARD_SET_FIELDS_TOOL, toolName, fields });
  };
}

export function setAgentExecutorSeleted (agent) {
  return async (dispatch) => {
    dispatch({ type: types.AGENT_EXECUTIONS_SET_SELECTED, agent });
  };
}

export function getManifest () {
  return async (dispatch) => {
    try {
      const data = await api.manage.fetchManifest();
      dispatch({ type: types.AGENTS_GET_MANIFEST, data });
    } catch (e) {
      dispatch(somethingWentWrong(e));
    }
  };
}

export const setAgent = (data) => (dispatch) => {
  dispatch({ type: types.SET_NEW_AGENT, data });
};

export function clearAgent () {
  return (dispatch) => {
    dispatch({ type: types.RESET_NEW_AGENT });
  };
}

export function setTriggerAgentWs (workspaces, executor) {
  return (dispatch) => {
    const newExecutor = executor;
    Object.assign(newExecutor.parameters_data, { workspaces_names: workspaces });
    dispatch({ type: types.AGENTS_RUN_SET_WORKSPACES, newExecutor });
  };
}

export function addTriggerAgentWs (ws, executor) {
  return (dispatch, getState) => {
    const addedWorkspaces = selectTriggerAgentWs(getState(), executor?.id);
    const allWorkspaces = selectActiveWorkspaces(getState());

    const newWs = allWorkspaces.find((workspace) => workspace.name === ws)?.name;
    if (newWs) {
      const newWorkspaces = [
        ...addedWorkspaces.filter((workspace) => workspace !== ws),
        newWs
      ];
      dispatch(setTriggerAgentWs(newWorkspaces, executor));
    }
  };
}

export function removeTriggerAgentWs (ws, executor) {
  return (dispatch, getState) => {
    const addedWorkspaces = selectTriggerAgentWs(getState(), executor?.id);
    const newWorkspaces = addedWorkspaces.filter((w) => w !== ws);
    dispatch(setTriggerAgentWs(newWorkspaces, executor));
  };
}

export function setSeverityRange (severityRange) {
  return (dispatch) => {
    dispatch({ type: types.AGENTS_RUN_SET_SEVERITY_RANGE, severityRange });
  };
}

export function setResolveHostname (resolveHostname) {
  return (dispatch) => {
    dispatch({ type: types.AGENTS_RUN_SET_RESOLVE_HOSTNAME, resolveHostname });
  };
}

export function setResolveHostnameInDetail (resolveHostname, executor) {
  return (dispatch) => {
    const newExecutor = executor;
    Object.assign(newExecutor.parameters_data, { resolve_hostname: resolveHostname });
    dispatch({ type: types.AGENTS_DETAIL_SET_RESOLVE_HOSTNAME, newExecutor });
  };
}

export function setSeverityRangeInDetail (min, max, executor) {
  return (dispatch) => {
    const newExecutor = executor;
    Object.assign(newExecutor.parameters_data, { min_severity: min, max_severity: max });
    dispatch({ type: types.AGENTS_DETAIL_SET_SEVERITY_RANGE, newExecutor });
  };
}

// Click Logic

const addDeleteController = (agents, agentList) => {
  const allAgentsAreSelected = agents.every((testAgent) => agentList.some((agent) => agent.id === testAgent.id));

  if (allAgentsAreSelected) return [types.AGENT_UNSELECTED, agents];
  return [types.AGENT_SELECTED, agents];
};

const selectCalculator = (e, agent, agentList, areAgentsSelected) => (dispatch, getState) => {
  const pivot = selectLastSelected(getState());
  const index = agentList.findIndex((el) => el.id === agent.id);
  const agentsSelected = selectAgentsSelected(getState());

  dispatch({ type: types.NEW_PIVOT, payload: index });
  if (e.shiftKey && pivot !== -1 && areAgentsSelected) {
    const start = Math.min(pivot, index);
    const end = Math.max(pivot, index) + 1;
    const agents = agentList.slice(start, end);
    const [type, payload] = addDeleteController(agents, agentsSelected);
    return dispatch({ type, payload });
  }
  const [type, payload] = addDeleteController([agent], agentsSelected);
  return dispatch({ type, payload });
};

export const selectRow = (e, agent) => (dispatch, getState) => {
  const allAgents = selectAgentList(getState());
  const agentSelected = selectAgentsSelected(getState());
  dispatch(selectCalculator(e, agent, allAgents, agentSelected.length > 0));
};

export const unSelectAll = () => (dispatch) => dispatch({ type: types.UNSELECT_ALL_AGENT });

export const selectAllAgents = () => (dispatch, getState) => {
  const agentsList = selectAgentList(getState());
  const agentsSelected = selectAgentsSelected(getState());

  if (agentsSelected.length === agentsList.length) return dispatch(unSelectAll());

  return dispatch({ type: types.SELECT_ALL_AGENT, agentsList });
};

export const selectAllAgentExecutions = () => (dispatch, getState) => {
  const agentExecutionsList = selectAgentExecutions(getState());
  return dispatch({ type: types.SELECT_ALL_AGENT_EXECUTIONS, agentExecutionsList });
};

export const toggleAgentsTab = () => (dispatch) => dispatch({ type: types.TOGGLE_AGENTS_TAB });

export function changeStateAgent (agent) {
  return async (dispatch) => {
    try {
      dispatch(fetching());
      const payload = {
        name: agent?.name,
        active: !agent?.active
      };
      await api.agents.changeStateAgent(agent.id, payload);
      dispatch(getUserAgents());
      dispatch(closeModal(MODAL_RUN_USER_AGENT));
      dispatch({ type: types.AGENTS_SUCCESS });
    } catch (e) {
      dispatch(somethingWentWrong(e));
    }
  };
}

export function removeAgent (agent) {
  return async (dispatch) => {
    try {
      dispatch(fetching());
      await api.agents.removeAgent(agent.id);
      dispatch(getUserAgents());
      dispatch(unSelectAll());
    } catch (e) {
      dispatch(somethingWentWrong());
    }
  };
}

export function removeSelectedAgents () {
  return async (dispatch, getState) => {
    try {
      const showCloudAgents = SelectShowCloudAgents(getState());
      const agentsSelected = selectAgentsSelected(getState());
      dispatch(fetching());

      const removeFunction = showCloudAgents ? api.agents.removeCloudAgent : api.agents.removeAgent;

      const removalPromises = agentsSelected.map(agent => removeFunction(agent.id));
      await Promise.all(removalPromises);

      dispatch(showCloudAgents ? getCloudAgents() : getUserAgents());
      dispatch(unSelectAll());
    } catch (e) {
      dispatch(somethingWentWrong());
    }
  };
}

export function setPageUserAgents (pageNumber) {
  return (dispatch) => {
    dispatch(setPageNumber('userAgents', pageNumber));
    dispatch(getUserAgents());
  };
}

export function setPageCloudAgents (pageNumber) {
  return (dispatch) => {
    dispatch(setPageNumber('cloudAgents', pageNumber));
    dispatch(getCloudAgents());
  };
}

export function setPageAgentExecutions (pageNumber) {
  return (dispatch) => {
    dispatch(setPageNumber('agentExecutions', pageNumber));
    dispatch(getAgentsExecutions());
  };
}

export function setToolSelected (tool) {
  return (dispatch) => {
    dispatch({ type: types.AGENTS_SET_TOOL_SELECTED, tool });
  };
}

export const saveAgentToolData = (id, data) => async (dispatch, getState) => {
  try {
    const state = getState();
    const workspaces = selectTriggerAgentWs(state, id);
    const min_severity = selectTriggerMinSeverity(state, id);
    const max_severity = selectTriggerMaxSeverity(state, id);
    const resolveHostname = selectTriggerResolveHostname(state, id);
    const agentId = selectTriggerAgentId(state);

    const parameters_data = {
      ...data,
      min_severity,
      max_severity,
      resolve_hostname: resolveHostname,
      workspaces_names: workspaces
    };

    if (id) {
      await api.agents.saveToolData({ executor_id: id, parameters_data });
      dispatch({ type: types.SAVE_AGENT_TOOL_DATA_SUCCESS });
      dispatch(getUserAgent(agentId));
    }
  } catch (e) {
    dispatch(somethingWentWrong(e));
  }
};

export const clearSuccessMessage = () => (dispatch) => {
  dispatch({ type: types.AGENTS_CLEAR_SUCCESS });
};

export const resetDetail = () => (dispatch) => {
  dispatch({ type: types.AGENTS_RESET_DETAIL });
};

export const saveAndRun = (id, data, toolName) => async (dispatch) => {
  try {
    await dispatch(saveAgentToolData(id, data));
    dispatch(runUserAgentTool(toolName, data));
  } catch (e) {
    dispatch(somethingWentWrong(e));
  }
};

export function getAgentsExecutions () {
  return async (dispatch, getState) => {
    try {
      dispatch(fetching());
      const queryParam = selectQueryParam('agentExecutions', getState());
      const response = await api.agents.getAgentsExecutions(queryParam);
      dispatch({ type: types.GET_AGENT_EXECUTIONS, response });
    } catch (e) {
      dispatch(somethingWentWrong());
    }
  };
}

export function getResultExecutions (commandId, workspaceName) {
  return async (dispatch, getState) => {
    try {
      dispatch(fetching());
      const vulns = await api.agents.getResultExecutions('vulns', commandId);
      const services = await api.agents.getResultExecutions('services', commandId);
      const hosts = await api.agents.getResultExecutions('hosts', commandId);

      const results = {
        vulns: vulns?.count,
        services: services?.count,
        hosts: hosts?.count,
        workspaceName
      };

      dispatch({ type: types.GET_RESULT_EXECUTIONS, results });
    } catch (e) {
      dispatch(somethingWentWrong());
    }
  };
}

export function getResultsExecutions (workspacesCommands) {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: types.RESET_RESULT_EXECUTIONS });
      workspacesCommands.map(({ command_id, workspace_name }) => dispatch(getResultExecutions(command_id, workspace_name)));
    } catch (e) {
      dispatch(somethingWentWrong());
    }
  };
}

export function deleteAgentExecutions () {
  return async (dispatch, getState) => {
    try {
      dispatch(fetching());
      const state = getState();
      const agentsExecutionsSelected = selectAgentExecutionSelected(state).map((ae) => ae.id);

      const data = {
        ids: agentsExecutionsSelected
      };

      await api.agents.deleteAgentsExecutions(data);
      dispatch(getAgentsExecutions());
    } catch (e) {
      dispatch(somethingWentWrong());
    }
  };
}

export const multipleAgentExecutionSetSelected = (agent) => (dispatch) => dispatch({ type: types.MULTIPLE_AGENT_EXECUTIONS_SET_SELECTED, agent });

export const unselectAllAgentExecutions = () => (dispatch) => dispatch({ type: types.UNSELECT_ALL_AGENTS_EXECUTIONS });

export const unselectAgentExecution = (agent) => (dispatch) => dispatch({ type: types.UNSELECT_AGENTS_EXECUTIONS, agent });

export function runAgentFromLogs () {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const executionSelected = selectAgentExecutionSelected(state);
      const agentId = get(executionSelected, '[0].agent_id', '');
      const executorData = get(executionSelected, '[0].parameters_data.executor_data', {});
      const workspacesCommands = get(executionSelected, '[0].parameters_data.workspaces_commands', []);

      dispatch(fetching());

      const payload = {
        workspaces_names: workspacesCommands.map((e) => e.workspace_name),
        executor_data: {
          ...executorData,
          agent_id: agentId
        }
      };

      await api.agents.runUserAgentTool(agentId, payload);

      dispatch(getAgentsExecutions());
      dispatch(closeModal(MODAL_RUN_USER_AGENT));
      dispatch({ type: types.AGENTS_SUCCESS });
    } catch (e) {
      dispatch(somethingWentWrong(e));
    }
  };
}

export const setOrderByUserAgents = (sorting) => (dispatch) => {
  dispatch(setOrderBy('userAgents', sorting));
  dispatch(getUserAgents());
};

export const setOrderByAgentsExecutions = (sorting = [], entity) => (dispatch) => {
  if (sorting.length > 0) dispatch(setOrderBy('agentExecutions', sorting));
  else dispatch(clearOrderBy('agentExecutions'));
  dispatch(getAgentsExecutions(entity));
};
